import { AnimatePresence, motion } from "framer-motion";
import { InputText, InputTextProps } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import {
    ChangeEvent,
    forwardRef,
    useImperativeHandle,
    useRef,
    useState,
} from "react";

interface SearchInputTextProps extends InputTextProps {
    inputId?: string;
    containerClassName?: string | null;
    isAnimated?: boolean;
    placeholder?: string;
}

const SearchInputText = forwardRef<HTMLInputElement, SearchInputTextProps>(
    (props: SearchInputTextProps, ref) => {
        const {
            className,
            containerClassName = null,
            value,
            isAnimated = true,
            inputId,
            onChange,
            placeholder,
            ...rest
        } = props;

        const inputRef = useRef<HTMLInputElement>(null);

        const [isFocused, setIsFocused] = useState(false); // Needed to expand on focus (even when empty)(changes via ref won't update the UI)

        useImperativeHandle(ref, () => inputRef.current as HTMLInputElement); // Expose ref to parent

        const hasValue = !!value;
        const isExpanded = isAnimated ? isFocused || hasValue : true;

        const handleClear = () => {
            onChange?.({
                target: { value: "" },
            } as ChangeEvent<HTMLInputElement>);
            inputRef.current?.focus();
        };

        return (
            <div
                className={classNames(
                    "relative cursor-text min-w-0",
                    containerClassName
                )}
            >
                <i className="pi pi-search absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-text-500/60" />
                <InputText
                    id={inputId}
                    ref={inputRef}
                    value={value}
                    onChange={onChange}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    className={classNames(
                        "w-52 max-w-full px-9 transition-all duration-200 ease-in-out",
                        {
                            "md:w-9 md:px-1.5 md:placeholder:opacity-0":
                                !isExpanded,
                        },
                        className
                    )}
                    placeholder={placeholder}
                    onKeyDown={(e) => {
                        if (e.key === "Escape" && value) handleClear();
                    }}
                    {...rest}
                />
                <AnimatePresence>
                    {hasValue && (
                        <motion.button
                            key="clear-icon"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={handleClear}
                            className="pi pi-times absolute right-0 top-1/2 -translate-y-1/2 cursor-pointer text-text-500/60 hover:text-text-500 bg-transparent h-full pr-3.5 pl-1.5"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        />
                    )}
                </AnimatePresence>
            </div>
        );
    }
);

SearchInputText.displayName = "SearchInputText";

export default SearchInputText;
